import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import FadeIn from 'react-fade-in';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import useStateRef from 'react-usestateref';
import { layoutDefaults } from '../../utils/layout-defaults';
import { parseItemDescription } from '../../utils/manager/item-manager';
import {
  parseMinMaxValue,
  parseValue,
} from '../../utils/manager/price-manager';
import { Hint } from '../hint';
import { Note } from './note';
import { OptionGroupContainer } from './option-group-container';
import { OrderingImage } from './ordering-image';
import { SelectPrice } from './price-select';
import { SelectQuantity } from './quantity-select';

function OrderModalBody({
  analyticsFeatureUseEvent,
  category,
  item,
  onChange,
  onDefaultSet,
  symbol,
  isSelfOrderingEnabled,
}) {
  const [order, setOrder] = useState({});
  const [baseValue, setBaseValue] = useState(0);
  const [
    selectedsByOptionGroupId,
    setSelectedsByOptionGroupId,
    selectedsByOptionGroupIdRef,
  ] = useStateRef({});
  const classes = useStyles();

  const { t } = useTranslation();

  const selectedOptions = useMemo(
    () =>
      _.values(selectedsByOptionGroupId)
        .reduce((acc, array) => acc.concat(array), [])
        .filter((o) => o != null),
    [selectedsByOptionGroupId]
  );

  const computedOptionGroups = useMemo(
    () => item?.option_groups ?? category?.option_groups,
    [item?.option_groups, category?.option_groups]
  );

  useEffect(() => {
    setOrder({ ...order, item });
  }, [item]);

  useEffect(() => {
    setOrder({ ...order, options: selectedOptions });
  }, [selectedOptions]);

  useEffect(() => {
    if (category != null && item != null && !_.isEmpty(computedOptionGroups)) {
      loadOptionGroups();
    }
  }, [category, item, computedOptionGroups]);

  useEffect(() => {
    loadOrder();
  }, [computedOptionGroups]);

  useEffect(() => {
    if (order != null && category != null) {
      loadBaseValue();
    }
  }, [order, category, item]);

  useEffect(() => {
    if (order != null) {
      onChange?.call(this, order);
    }
  }, [order]);

  function loadOrder() {
    const lOrder = { item, options: [] };

    const hasRequiredOption =
      computedOptionGroups?.find((og) => og.required_min > 0) != null;
    const isSinglePrice = item.prices?.length === 1;

    if (isSinglePrice) {
      lOrder.price = _.head(item.prices);
    }

    if (hasRequiredOption || lOrder.price == null) {
      lOrder.quantity = 1;
    }

    onDefaultSet?.call(this, lOrder);
    setOrder(lOrder);
  }

  function loadBaseValue() {
    let lBaseValue = order?.price?.value;
    if (lBaseValue == null) {
      lBaseValue = _.min(item?.prices?.map((p) => p.value ?? 0));
    }

    lBaseValue =
      (lBaseValue ?? 0) +
      parseMinMaxValue(category, item, {
        bypassItemPrices: lBaseValue != null,
        computeMin: true,
      });

    setBaseValue(lBaseValue);
  }

  function loadOptionGroups() {
    const lSelectedsByOptionGroupId = computedOptionGroups.reduce((acc, og) => {
      acc[og.id] = [...Array(og.required_max).fill(null)];
      return acc;
    }, {});

    setSelectedsByOptionGroupId(lSelectedsByOptionGroupId);

    // computedOptionGroups?.forEach((optionGroup) => {
    //   console.log(`optionGroup`);
    //   console.log(optionGroup);
    // });
  }

  const description = useMemo(
    () => parseItemDescription(item, category),
    [item, category]
  );

  const total = useMemo(() => {
    // const value = order?price?value ?? _.head(item.prices)?.value;
    // const value = order?.price?.value ?? baseValue;
    const value = baseValue;
    const add =
      order?.options
        ?.filter((option) => option != null)
        .reduce((acc, curr) => acc + (curr?.price?.value ?? 0), 0) ?? 0;
    const quantity =
      parseInt(order?.quantity ?? 0, 10) === 0 ? 1 : order?.quantity;

    const result = (value + add) * quantity;
    return parseValue(_.isNaN(result) ? 0 : result);
  }, [
    baseValue,
    // item.prices,
    order?.quantity,
    order?.options,
    // order?.price?.value,
  ]);

  const shouldShowTitles = useMemo(
    () => computedOptionGroups?.find((og) => og.required_max > 1) != null,
    [computedOptionGroups]
  );

  const onChangeOption = (optionGroup, option, index) => {
    const clone = _.cloneDeep(selectedsByOptionGroupIdRef.current);
    clone[optionGroup.id][index] = option;
    setSelectedsByOptionGroupId(clone);

    // setUnderError(false);
  };

  const onChangePrice = (price) => {
    setOrder({ ...order, price });
  };

  const onChangeQuantity = (quantity) => {
    setOrder({ ...order, quantity });
  };

  const onChangeNote = (note) => {
    if (_.isEmpty(note)) {
      setOrder({ ..._.omit(order, 'note') });
    } else {
      setOrder({ ...order, note });
    }
  };

  return (
    <div className={classes.container}>
      {category?.name?.trim().toLowerCase() !==
        item?.name?.trim().toLowerCase() && (
        <FadeIn className={classes.categoryTitle} delay={200}>
          <span>{category?.name}</span>
        </FadeIn>
      )}
      <FadeIn className={classes.title} delay={300}>
        <div>
          <span>{item?.name}</span>{' '}
          <Hint className={classes.hint} value={item.hint} showParenthesis />
        </div>
      </FadeIn>

      <OrderingImage
        image={item?.image}
        analyticsFeatureUseEvent={analyticsFeatureUseEvent}
      />

      {description && (
        <div className={classes.description}>
          <span>{description}</span>{' '}
        </div>
      )}

      <div className={classes.controls}>
        {!_.isEmpty(computedOptionGroups) &&
          computedOptionGroups.map((optionGroup) => (
            <OptionGroupContainer
              key={optionGroup.id}
              shouldShowTitles={shouldShowTitles}
              titleClassName={classes.groupTitle}
              optionGroup={optionGroup}
              category={category}
              item={item}
              selectedOptions={selectedsByOptionGroupId[optionGroup.id]}
              onChangeOption={onChangeOption}
            />
          ))}
        {shouldShowTitles && item?.prices?.length > 1 && (
          <div className={classes.groupTitle}>{t('ordering.portion')}</div>
        )}
        <SelectPrice
          className={classes.select}
          prices={item.prices}
          onChange={onChangePrice}
        />

        {shouldShowTitles && (
          <div className={classes.groupTitle}>{t('ordering.amount')}</div>
        )}
        <SelectQuantity
          className={classes.select}
          value={order?.quantity}
          onChange={onChangeQuantity}
          isSelfOrderingEnabled={isSelfOrderingEnabled}
        />
      </div>
      <div className={classes.footerContainer}>
        <div className={classes.price}>
          <span>
            {symbol} {total}
          </span>
        </div>

        <div>
          {isSelfOrderingEnabled && (
            <Note
              analyticsFeatureUseEvent={analyticsFeatureUseEvent}
              onChange={onChangeNote}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    // border: '1px solid green',
  },
  categoryTitle: {
    fontSize: layoutDefaults.fontSize,
    // lineHeightStep: 0.5,
    // fontWeight: "bold",
    // fontSize: layoutDefaults.smallFontSize,
    // marginTop: layoutDefaults.smallVerticalMargin,

    // border: "1px solid red",
  },
  title: {
    fontSize: layoutDefaults.biggerFontSize,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: layoutDefaults.verticalMargin,
    // marginTop: 20,
    // border: "1px solid red",
  },
  hint: {
    fontSize: layoutDefaults.fontSize,
    // whiteSpace: "normal",
    fontWeight: 'normal',
    // color: "black",
    opacity: layoutDefaults.opacity,
    // marginLeft: layoutDefaults.smallHorizontalMargin,
  },
  description: {
    fontSize: layoutDefaults.fontSize,
    marginTop: layoutDefaults.smallerVerticalMargin,
    marginBottom: layoutDefaults.verticalMargin,
    opacity: layoutDefaults.opacity,
  },
  singleOption: {
    color: 'black',
  },
  singleOptionHint: {
    marginLeft: layoutDefaults.smallerHorizontalMargin,
  },
  controls: {
    marginTop: layoutDefaults.smallerVerticalMargin,
  },
  groupTitle: {
    // color: layoutDefaults.imagePlaceholderColor,
    marginTop: layoutDefaults.verticalMargin,
    fontSize: layoutDefaults.smallestFontSize,
    // fontVariant: 'all-small-caps',
    textTransform: 'uppercase',
    opacity: layoutDefaults.opacity,
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'column-reverse',
    minHeight: '33px',
  },
  price: {
    pointerEvents: 'none',
    fontSize: layoutDefaults.fontSize,
    opacity: layoutDefaults.opacity,
    whiteSpace: 'nowrap',
    textAlign: 'right',

    marginTop: -21,
    marginBottom: layoutDefaults.verticalMargin,

    // backgroundColor: "yellow",
    // zIndex: -11,
  },
});

// const OrderingRef = forwardRef(Ordering);

export { OrderModalBody };
